import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AvisoDeConfiguracion } from './AvisoDeConfiguracion.tsx';
import { TopBar } from './TopBar.tsx';

interface AppShellProps {
  children: ReactNode;
  /**
   * Segundo nivel de navegación, si la página tiene sub-secciones.
   *
   * Si no viene, esa fila **no se renderea**: una banda vacía con su borde se
   * lee como un error de layout, no como una ausencia.
   *
   * Es un slot y no una lista de tabs porque el shell no puede conocer el
   * estado ni las rutas de cada página — recibe nodos ya construidos, que es
   * lo único que lo hace servir para las diez.
   */
  sectionTabs?: ReactNode;
}

/**
 * Marco de la aplicación: barra superior + contenido a lo ancho.
 *
 * Reemplaza el sidebar vertical de 240px. El ancho recuperado es el objetivo
 * del cambio: las tablas del CRM entraban justas y ahora respiran.
 */
export function AppShell({ children, sectionTabs }: AppShellProps) {
  const { pathname } = useLocation();
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-app">
      {/* Se preserva del shell anterior: es lo primero que encuentra alguien
          navegando con teclado, y saltearlo obliga a tabular toda la nav. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:bg-surface-raised focus:p-4 focus:text-content-brand"
      >
        Saltar al contenido
      </a>

      <TopBar />
      {sectionTabs ?? null}

      {/*
       * El que scrollea es el DIV de adentro, no el `<main>`.
       *
       * Parece equivalente y no lo es. Varias páginas —el workspace de
       * conversaciones, el detalle de contacto— arman su layout con `h-full` y
       * paneles que scrollean por dentro. `h-full` necesita que el padre tenga
       * una altura ACOTADA: si el contenedor crece con su contenido, `h-full`
       * no acota nada, los paneles se estiran y termina bajando la página
       * entera en vez del panel.
       *
       * `flex-1` + `min-h-0` es lo que le da esa altura: ocupa lo que sobra y
       * puede encoger por debajo de su contenido. Sin `min-h-0` un hijo flex
       * nunca baja de su tamaño natural, y el desborde vuelve.
       */}
      <main id="main" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-auto p-6">
          {/* En `/alta` sería redundante: ahí YA está configurando. */}
          {!pathname.startsWith('/alta') && <AvisoDeConfiguracion />}
          {children}
        </div>
      </main>
    </div>
  );
}
